import { useEffect, useState } from "react";
import { getAuthTokenForRequest } from "@/lib/api";

type CacheEntry = Promise<string | null> | { url: string } | { url: null };

const cache = new Map<string, CacheEntry>();
let generation = 0;
let abortController = new AbortController();

export function revokeAllAccountPhotos(): void {
  generation += 1;
  abortController.abort();
  abortController = new AbortController();
  for (const entry of cache.values()) {
    if (entry && "url" in entry && entry.url) {
      URL.revokeObjectURL(entry.url);
    }
  }
  cache.clear();
}

export function invalidateAccountPhoto(): void {
  for (const entry of cache.values()) {
    if (entry && "url" in entry && entry.url) {
      URL.revokeObjectURL(entry.url);
    }
  }
  cache.clear();
}

async function loadAccountPhoto(version?: string | null): Promise<string | null> {
  if (!version) return null;
  const cached = cache.get(version);
  if (cached !== undefined) {
    if (cached instanceof Promise) return cached;
    return cached.url;
  }

  const startGen = generation;
  const { signal } = abortController;
  const promise = (async (): Promise<string | null> => {
    try {
      const token = getAuthTokenForRequest();
      const resp = await fetch("/api/me/profile-photo", {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal,
      });
      if (generation !== startGen) return null;
      if (!resp.ok) {
        cache.set(version, { url: null });
        return null;
      }
      const blob = await resp.blob();
      if (generation !== startGen) return null;
      const url = URL.createObjectURL(blob);
      if (generation !== startGen) {
        URL.revokeObjectURL(url);
        return null;
      }
      cache.set(version, { url });
      return url;
    } catch {
      if (generation === startGen) cache.set(version, { url: null });
      return null;
    }
  })();

  cache.set(version, promise);
  return promise;
}

export function useAccountPhoto(version?: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!version) {
      setUrl(null);
      return;
    }
    let active = true;
    setUrl(null);
    loadAccountPhoto(version).then((resolved) => {
      if (active) setUrl(resolved);
    });
    return () => {
      active = false;
    };
  }, [version]);

  return url;
}
